import TextFieldWrapper from '@/components/common/TextFieldWrapper';
import BlockDebugView from '@/components/debug/application/BlockDebugView';
import ErrorNodeWrapper from '@/components/validation/ErrorNodeWrapper';
import { MetadataBlockContext } from '@/context/MetadataBlockContext';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { useViewportContext } from '@/hooks/useViewport';
import { SelectNode } from '@/types/backendNode';
import { updateNode } from '@/util/application';
import { getColorFromString } from '@/util/color';
import { ModeStandby } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Box, Card, CardHeader, DialogContent, IconButton, Tab, Tooltip, useTheme } from '@mui/material';
import { FC, useState } from 'react';
import ActionsView from './ActionsView';
import DetailsView from './DetailsView';
import EventsView from './EventsView';

type NodeSettingsViewProps = {
  node: SelectNode;
  pinNode: (isPinned: boolean) => void;
  onClose: () => void;
};

const NodeSettingsView: FC<NodeSettingsViewProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { setCenterOnNode } = useViewportContext();

  const backendNode = application.blocks.nodes[props.node.id.toString()];

  const theme = useTheme();
  const { metadata } = useMetadata();
  const [openDebug, setOpenDebug] = useState(false);
  const [tabValue, setTabValue] = useState('1');
  const metaBlock = metadata.blocks.find((block) => block.type === backendNode?.type);
  const blockStyle = metaBlock?.style;
  const headerColor = getColorFromString(blockStyle || backendNode?.type, theme.palette.mode === 'dark' ? 900 : 200);

  if (!backendNode) {
    props.onClose();
    return <></>;
  }

  return (
    <MetadataBlockContext.Provider
      value={{
        metadataBlock: metaBlock
      }}
    >
      <Card
        variant='outlined'
        sx={{
          flexGrow: 1,
          overflow: 'inherit'
        }}
      >
        <CardHeader
          action={
            <>
              <Tooltip title={'Debug'} placement='bottom' arrow>
                <IconButton onClick={() => setOpenDebug(!openDebug)}>
                  <CodeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={'Go to node'} placement='bottom' arrow>
                <IconButton onClick={() => setCenterOnNode(props.node.id.toString())}>
                  <ModeStandby />
                </IconButton>
              </Tooltip>
              <Tooltip title={props.node.isPinned ? 'Unpin window' : 'Pin window'} placement='bottom' arrow>
                <IconButton onClick={() => props.pinNode(!props.node.isPinned)}>
                  {props.node.isPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title={'Close window'} placement='bottom' arrow>
                <IconButton onClick={() => props.onClose()}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </>
          }
          title={
            <TextFieldWrapper
              value={backendNode.label}
              onChange={(event) => {
                if (backendNode.label !== event.currentTarget.value) {
                  setApplication(updateNode(props.node.id, 'label', event.currentTarget.value, application));
                  setUpdateNodes([props.node.id.toString()]);
                }
              }}
              size='small'
              fullWidth
            />
          }
          subheader={
            <ErrorNodeWrapper
              errors={metaBlock?.obsolete ? [{ severity: 'warning', message: metaBlock?.obsolete }] : undefined}
            >
              <code> {backendNode.type}</code> <code>ID:{props.node.id}</code>
            </ErrorNodeWrapper>
          }
          sx={{
            color: theme.palette.getContrastText(headerColor),
            bgcolor: headerColor,
            gap: 1
          }}
        />
        <DialogContent sx={{ p: 1, pt: 0, overflow: 'inherit' }}>
          <BlockDebugView blockProps={props} open={openDebug} />
          <TabContext value={tabValue}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 1
              }}
            >
              <TabList onChange={(_, newValue) => setTabValue(newValue)}>
                <Tab label='Details' value='1' />
                {blockStyle !== 'endblock' && <Tab label='Actions' value='2' />}
                {metaBlock?.event_handlers && <Tab label='Events' value='3' />}
              </TabList>
            </Box>
            <TabPanel value='1' sx={{ p: 0 }}>
              <DetailsView id={props.node.id} parentId={props.node.parentId} />
            </TabPanel>
            <TabPanel value='2' sx={{ p: 0 }}>
              {blockStyle !== 'endblock' && <ActionsView id={props.node.id} parentId={props.node.parentId} />}
            </TabPanel>
            <TabPanel value='3' sx={{ p: 0 }}>
              {metaBlock?.event_handlers && <EventsView id={props.node.id} parentId={props.node.parentId} />}
            </TabPanel>
          </TabContext>
        </DialogContent>
      </Card>
    </MetadataBlockContext.Provider>
  );
};

export default NodeSettingsView;
